import React, { useEffect, useRef, useState } from 'react';
import { uc } from '../unifiedContactsApi';

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;
const LIMIT = 8;

// The look of components/ui/input, for forms whose other fields use it (ticket 188).
export const FIELD_CLASS = 'flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm';

export const primaryEmail = (c) => {
  const list = c?.emails || [];
  return (list.find((e) => e.is_primary) || list[0])?.value || '';
};

// Ticket 155: free-text input that searches the tenant's contacts as you type
// (name substring, exact email, exact phone). The input is always free text:
// results only fill anything on a pick. Only typing searches; a pick or a
// programmatic `value` change (loaded worksheet) does not. `onCreateNew` adds a
// trailing "+ New contact" row (hidden when not passed) that gets the typed text.
export default function ContactSearchInput({ value, onChange, onPick, onCreateNew, testid, className, placeholder, id }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const reqRef = useRef(0);
  const typedRef = useRef(false); // true only when the latest value change came from typing
  const text = value ?? '';

  useEffect(() => {
    const q = text.trim();
    if (!typedRef.current) return undefined;
    typedRef.current = false;
    const req = ++reqRef.current;
    if (q.length < MIN_CHARS) {
      setItems([]);
      setOpen(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const r = await uc.search(q, LIMIT);
        if (reqRef.current !== req) return;
        setItems((r && r.contacts) || []);
        setActive(-1);
        setOpen(true);
      } catch (e) {
        if (reqRef.current === req) { setItems([]); setOpen(false); }
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const listId = `${testid}-results`;
  const close = () => { setOpen(false); setActive(-1); };
  const rows = items.length + (onCreateNew ? 1 : 0); // the "+ New contact" row is the last row
  const pick = (c) => {
    typedRef.current = false;
    reqRef.current += 1; // drop any response still in flight
    if (onPick) onPick(c);
    setItems([]);
    close();
  };
  const createNew = () => {
    typedRef.current = false;
    reqRef.current += 1;
    close();
    onCreateNew(text.trim());
  };
  const activate = (i) => (i < items.length ? pick(items[i]) : createNew());
  const onKeyDown = (e) => {
    if (!open || rows === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % rows);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a <= 0 ? rows - 1 : a - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      activate(active);
    } else if (e.key === 'Escape') {
      close();
    }
  };
  const optId = (i) => (i < items.length ? `${testid}-option-${i}` : `${testid}-new`);

  return (
    <div className="relative">
      <input
        type="text"
        id={id}
        value={text}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => { typedRef.current = true; onChange(e.target.value); }}
        onKeyDown={onKeyDown}
        onBlur={() => { reqRef.current += 1; close(); }} // a late response must not reopen the list
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? optId(active) : undefined}
        data-testid={testid}
        className={className}
      />
      {open && rows > 0 && (
        <ul
          id={listId}
          role="listbox"
          data-testid={listId}
          className="absolute z-50 left-0 right-0 mt-1 rounded-md border border-slate-200 bg-white shadow-lg text-sm overflow-hidden"
        >
          {items.length === 0 && (
            <li role="presentation" className="px-3 py-1.5 text-slate-500" data-testid={`${testid}-empty`}>
              No matching contacts
            </li>
          )}
          {items.map((c, i) => {
            const email = primaryEmail(c);
            return (
              <li
                key={c.contact_id || i}
                id={optId(i)}
                role="option"
                aria-selected={i === active}
                data-testid={`${testid}-option-${i}`}
                onMouseDown={(e) => { e.preventDefault(); pick(c); }}
                onMouseEnter={() => setActive(i)}
                className={`px-3 py-1.5 cursor-pointer ${i === active ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
              >
                <span>{c.display_name}</span>
                {email && <span className="ml-2 text-slate-500">{email}</span>}
              </li>
            );
          })}
          {onCreateNew && (
            <li
              id={optId(items.length)}
              role="option"
              aria-selected={active === items.length}
              data-testid={`${testid}-new`}
              onMouseDown={(e) => { e.preventDefault(); createNew(); }}
              onMouseEnter={() => setActive(items.length)}
              className={`px-3 py-1.5 cursor-pointer font-medium border-t border-slate-100 ${active === items.length ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
              style={{ color: '#070B5F' }}
            >
              + New contact
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
